import React from 'react';
import { formWithValidation } from '@edgebox/react-components';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { IsString, Matches, MaxLength } from 'class-validator';
import { Field } from 'formik';
import type { FieldProps } from 'formik';
import { BP_PRIMARY_BUTTON_CLASSES } from './brandPresenceClasses';

export class GovernanceCrawlContentUrlFormData {
  @IsString()
  @MaxLength(2000)
  @Matches(/^https?:\/\/\S+$/i, {
    message: 'Please enter a valid URL that starts with http:// or https://.',
  })
  url!: string;
}

const GovernanceCrawlContentUrlForm = formWithValidation<GovernanceCrawlContentUrlFormData, GovernanceCrawlContentUrlFormData>(
  (values) => plainToInstance(GovernanceCrawlContentUrlFormData, values),
  (entity) => instanceToPlain(entity) as GovernanceCrawlContentUrlFormData
);

export function normalizeCrawlUrlInput(input: string): string {
  const value = String(input || '').trim();
  if (!value) {
    return '';
  }

  try {
    const parsed = new URL(value);
    if (!/^https?:$/i.test(parsed.protocol)) {
      return value;
    }

    parsed.hash = '';
    return parsed.toString();
  } catch {
    return value;
  }
}

interface IGovernanceCrawlContentUrlInputFormProps {
  versionKey: string;
  disabled?: boolean;
  onAdd: (url: string) => void;
}

export function GovernanceCrawlContentUrlInputForm({ versionKey, disabled, onAdd }: IGovernanceCrawlContentUrlInputFormProps) {
  return (
    <GovernanceCrawlContentUrlForm
      key={versionKey}
      initialValues={plainToInstance(GovernanceCrawlContentUrlFormData, { url: '' })}
      onSubmit={async (values) => {
        const normalizedUrl = normalizeCrawlUrlInput(values.url);
        onAdd(normalizedUrl);
      }}
    >
      {() => (
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="flex-grow">
            <Field name={'url'}>
              {({ field, form }: FieldProps<string>) => (
                <input
                  {...field}
                  type={'text'}
                  placeholder={'https://example.com/path'}
                  disabled={disabled}
                  onBlur={(event) => {
                    const normalizedUrl = normalizeCrawlUrlInput(event.target.value);
                    form.setFieldValue('url', normalizedUrl, false);
                    form.setFieldTouched('url', true, true);
                  }}
                  className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 transition-all duration-150 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10"
                />
              )}
            </Field>
          </div>
          <button type="submit" disabled={disabled} className={BP_PRIMARY_BUTTON_CLASSES}>
            Add
          </button>
        </div>
      )}
    </GovernanceCrawlContentUrlForm>
  );
}
